import { createContext, useContext, useEffect, useState, useCallback } from "react";

const BASE = import.meta.env.BASE_URL;

export type Cohort = { id: number; name: string; slug: string; status?: string };

type Identity = "banker" | "investor" | "admin";

type CohortCtx = {
  cohorts: Cohort[];
  cohortId: number | null;
  setCohortId: (id: number) => void;
  isLoading: boolean;
  refresh: () => void;
};

const Ctx = createContext<CohortCtx | null>(null);

const STORAGE_KEY: Record<Identity, string> = {
  banker: "banker_active_cohort",
  investor: "investor_active_cohort",
  admin: "admin_active_cohort",
};

const ENDPOINT: Record<Identity, string> = {
  banker: "api/banker/me/cohorts",
  investor: "api/investor-portal/cohorts",
  // Admin sees every cohort — `/api/cohorts` returns the full list.
  admin: "api/cohorts",
};

function getAuthHeaders(identity: Identity): HeadersInit {
  if (identity === "banker") {
    const token = localStorage.getItem("banker_token") || "";
    return { "x-banker-token": token };
  }
  if (identity === "admin") {
    const token = localStorage.getItem("admin_token") || "";
    return { "x-admin-token": token };
  }
  return { "x-investor-token": localStorage.getItem("investor_token") || "" };
}

export function CohortProvider({ identity, children }: { identity: Identity; children: React.ReactNode }) {
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [cohortId, setCohortIdState] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = localStorage.getItem(STORAGE_KEY[identity]);
    return raw ? Number(raw) || null : null;
  });
  const [isLoading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}${ENDPOINT[identity]}`, {
        headers: getAuthHeaders(identity),
        credentials: identity === "investor" ? "include" : "same-origin",
      });
      if (!r.ok) { setCohorts([]); setLoading(false); return; }
      const j = await r.json();
      // /api/cohorts returns a bare array; the per-identity endpoints wrap it in `{cohorts: [...]}`
      const list: Cohort[] = Array.isArray(j) ? j : (j.cohorts || []);
      setCohorts(list);
      // Pick a valid active cohort
      setCohortIdState(prev => {
        if (prev && list.some(c => c.id === prev)) return prev;
        const first = list[0]?.id ?? null;
        if (first) localStorage.setItem(STORAGE_KEY[identity], String(first));
        return first;
      });
    } catch {
      setCohorts([]);
    } finally {
      setLoading(false);
    }
  }, [identity]);

  useEffect(() => { load(); }, [load]);

  const setCohortId = useCallback((id: number) => {
    localStorage.setItem(STORAGE_KEY[identity], String(id));
    setCohortIdState(id);
    // Notify any listeners using the same key (cross-tab + intra-tab)
    window.dispatchEvent(new CustomEvent("cohort-change", { detail: { identity, id } }));
  }, [identity]);

  return (
    <Ctx.Provider value={{ cohorts, cohortId, setCohortId, isLoading, refresh: load }}>
      {children}
    </Ctx.Provider>
  );
}

export function useActiveCohort(): CohortCtx {
  const v = useContext(Ctx);
  if (!v) {
    // Permissive fallback so non-wrapped components don't crash during refactor.
    return { cohorts: [], cohortId: null, setCohortId: () => {}, isLoading: false, refresh: () => {} };
  }
  return v;
}

/** Append cohortId to a URL if one is active. */
export function withCohort(url: string, cohortId: number | null | undefined): string {
  if (!cohortId) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}cohortId=${cohortId}`;
}
